import React, { createContext, ReactNode, useCallback, useContext, useMemo, useState } from "react";
import { Entry } from "../../../model/accounting/entry/Entry.js";
import { EntityFile } from "../../../model/files/EntityFile.js";
import { useHref } from "../../../../packages/router/href/HrefProvider.js";
import { useLoadEffect } from "../../../../react/load/useLoadEffect.js";
import { getEntry, getEntryFiles, removeEntry, removeEntryFile, uploadEntryFiles } from "../accountingApi.js";

type ViewState = {
  entry: Entry;
  files: EntityFile[];
  remove: () => void;
  removeFile: (fileId: string) => void;
  uploadFiles: (files: FileList) => Promise<void>;
  isUploading: boolean;
  uploadError?: string;
};

const ViewContext = createContext<ViewState | undefined>(undefined);

type Props = {
  entryId: string;
  children: ReactNode;
};

/**
 * Entry view state provider.
 */
export const ViewProvider = ({ entryId, children }: Props) => {
  const { setHref } = useHref();

  const result = useLoadEffect(`entry-${entryId}`, async () => {
    return {
      entry: await getEntry(entryId),
      files: await getEntryFiles(entryId)
    };
  });

  const entry = useMemo(() => (result.state === "success" ? result.data.entry : undefined), [result]);
  const files = useMemo(() => (result.state === "success" ? result.data.files : []), [result]);

  const [isUploading, setUploading] = useState<boolean>(false);
  const [uploadError, setUploadError] = useState<string>();

  const uploadFiles = useCallback(
    async (files: FileList) => {
      const data = new FormData();
      for (let i = 0; i < files.length; i++) {
        data.append("files", files[i]);
      }
      setUploadError("");
      setUploading(true);
      try {
        await uploadEntryFiles(entryId, data);
      } catch (err) {
        setUploadError(err + "");
      } finally {
        setUploading(false);
        result.reload();
      }
    },
    [entryId, result]
  );

  const removeFile = useCallback(
    async (fileId: string) => {
      if (confirm("Kustuta fail?")) {
        try {
          await removeEntryFile(entryId, fileId);
        } catch (err) {
          setUploadError(err + "");
        } finally {
          result.reload();
        }
      }
    },
    [entryId, result]
  );

  const remove = useCallback(async () => {
    if (confirm("Kustuta sisestus?")) {
      try {
        await removeEntry(entryId);
      } finally {
        setHref("/entries");
      }
    }
  }, [entryId, setHref]);

  if (entry === undefined) {
    return null;
  }

  const value: ViewState = { entry, files, removeFile, remove, uploadFiles, isUploading, uploadError };

  return <ViewContext.Provider value={value}>{children}</ViewContext.Provider>;
};

/**
 * Helper hook to access the entry view state.
 */
export const useEntryView = () => {
  const context = useContext(ViewContext);
  if (!context) {
    throw new Error("The entry view context is not provided.");
  }
  return context;
};
